import React, { useState, useEffect } from 'react';
import CurrencyInput from '../CurrencyInput';
import useDefaultModal from '../hooks/useDefaultModal';
import useConfirmModal from '../hooks/useConfirmModal';
import DefaultModal from './DefaultModal';
import ConfirmModal from './ConfirmModal';

// Colunas FNDE que queremos exibir (por correspondência parcial, case-insensitive)
const COLUNAS_FNDE = ['Valor Custeio', 'Valor Capital', 'Data da Ord. de Pagamento'];

/** Encontra o valor de uma coluna FNDE por correspondência parcial do nome */
export function getValorFnde(registro, chave) {
  const k = Object.keys(registro).find(c =>
    c.toLowerCase().includes(chave.toLowerCase())
  );
  return k ? registro[k] : '';
}

/** Converte data DD/MM/YYYY para YYYY-MM-DD, ou retorna como está se já no formato ISO */
export function toIso(val) {
  if (!val) return '';
  const m = val.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return val;
}

/** Converte valor monetário BR (1.234,56) para float */
function parseBRL(val) {
  if (!val) return 0;
  return parseFloat(String(val).replace(/\./g, '').replace(',', '.')) || 0;
}

/** Normaliza números de conta para comparação (remove formatação e zeros à esquerda)
 * Ex.: FNDE "0000388556" e cadastro "38855-6" -> "388556"; "000005433X" e "5433-X" -> "5433X" */
export function normalizeConta(val) {
  if (!val) return '';
  const t = String(val).toUpperCase().replace(/[^0-9X]/g, '');
  return t.replace(/^0+(?=\d)/, '');
}

/** Lê a conta informada pela página do FNDE (chave exata "Conta" quando existir) */
export function getContaFnde(registro) {
  if (!registro) return '';
  const keys = Object.keys(registro);
  const exata = keys.find(k => (k || '').trim().toLowerCase() === 'conta');
  const k = exata || keys.find(c => (c || '').toLowerCase().includes('conta'));
  return k ? (registro[k] || '') : '';
}

/** Indica quando a conta selecionada para exportação difere da conta informada
 * pelo FNDE (compara por dígitos, ignorando formatação e zeros à esquerda) */
export function contaDivergente(contaFnde, idConta) {
  const a = normalizeConta(contaFnde);
  const b = normalizeConta(idConta);
  if (!a || !b) return false;
  return a !== b;
}

/** Normaliza texto para comparação (remove acentos, pontuação e múltiplos espaços) */
function normalizeText(s) {
  if (!s) return '';
  return s.toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Tenta casar texto do programa FNDE com lista de programas do sistema */
function matchPrograma(texto, programas) {
  if (!texto) return null;
  const t = normalizeText(texto);
  if (!t) return null;
  // 1) tentativa de igualdade normalizada
  let found = programas.find(p => normalizeText(p.descricao) === t);
  if (found) return found;
  // 2) tentativa por inclusão (descrição do sistema contém texto FNDE)
  found = programas.find(p => normalizeText(p.descricao).includes(t));
  if (found) return found;
  // 3) tentativa inversa (texto FNDE contém descrição do sistema)
  found = programas.find(p => t.includes(normalizeText(p.descricao)));
  if (found) return found;
  // 4) tentativa por prefixo (números ou códigos no início)
  found = programas.find(p => {
    const np = normalizeText(p.descricao).split(' - ')[0];
    return np && t.startsWith(np);
  });
  if (found) return found;

  // 5) tentativa fuzzy por sobreposição de tokens (escolhe melhor candidato)
  const tokensT = new Set(t.split(' ').filter(Boolean));
  let best = null;
  let bestScore = 0;
  programas.forEach(p => {
    const np = normalizeText(p.descricao);
    const tokensP = new Set(np.split(' ').filter(Boolean));
    let score = 0;
    tokensT.forEach(tok => { if (tokensP.has(tok)) score += 1; });
    if (score > bestScore) { bestScore = score; best = p; }
  });
  // regras para aceitar candidato fuzzy: ao menos 1 token em comum e (ou) correspondência curta
  if (best && bestScore >= 1) {
    // Se houver apenas um token no FNDE e esse token for muito genérico, evite casamentos fracos
    if (tokensT.size === 1 && bestScore === 1) {
      // aceitar apenas se o token tem comprimento >= 3
      const only = Array.from(tokensT)[0];
      if (only.length >= 3) return best;
      return null;
    }
    return best;
  }

  // sem correspondência
  return null;
}


export function buildLinha(d, i, programas, acoes, contas) {
  const progFndeTxt = getValorFnde(d, 'Programa') || '';
  let matched = matchPrograma(progFndeTxt, programas);
  let idPrograma = matched ? String(matched.id) : '';

  // Fallback: se FNDE não trouxe Programa, tente inferir a partir da Destinação
  if (!matched) {
    const destinacaoTxt = (getValorFnde(d, 'Destinação') || '').toString();
    if (destinacaoTxt) {
      // exemplos: "PDDE / PDDE Básico - 1ª Parcela" -> tentar tokens 'PDDE', 'PDDE Básico'
      const parts = destinacaoTxt.split('/').map(p => p.trim()).filter(Boolean);
      // tentar cada parte para casar com programa
      for (const ptxt of parts) {
        const cand = matchPrograma(ptxt, programas);
        if (cand) { matched = cand; idPrograma = String(cand.id); break; }
        // também tente cortar por '-' e tentar a parte esquerda
        const left = ptxt.split('-')[0].trim();
        if (left && left !== ptxt) {
          const cand2 = matchPrograma(left, programas);
          if (cand2) { matched = cand2; idPrograma = String(cand2.id); break; }
        }
      }
    }
  }

  const acoesDisp = idPrograma ? acoes.filter(a => String(a.idPrograma) === idPrograma) : [];
  const contasDisp = idPrograma ? contas.filter(c => String(c.idPrograma) === idPrograma && c.debitar === 'SIM') : [];

  // Conta vinda da página do FNDE: casar com a conta cadastrada quando possível
  const contaFnde = getContaFnde(d);
  let idConta = '';
  if (contasDisp.length === 1) {
    idConta = contasDisp[0].numConta;
  } else if (contaFnde) {
    const alvo = normalizeConta(contaFnde);
    const match = contasDisp.find(c => normalizeConta(c.numConta) === alvo)
      || contas.find(c => String(c.idPrograma) === idPrograma && normalizeConta(c.numConta) === alvo);
    if (match) idConta = match.numConta;
  }

  // Se ainda não casou programa, tente inferir a ação a partir do texto da destinação
  let inferredAcaoId = '';
  if (!matched) {
    const destinacaoTxt = (getValorFnde(d, 'Destinação') || '').toString();
    if (destinacaoTxt) {
      const tokens = normalizeText(destinacaoTxt).split(' ').filter(Boolean);
      // procurar ação cujo texto contenha um dos tokens (apenas entre as ações disponíveis)
      if (acoes && acoes.length) {
        for (const a of acoes) {
          const na = normalizeText(a.descricao);
          for (const t of tokens) {
            if (t.length >= 3 && na.includes(t)) { inferredAcaoId = String(a.id); break; }
          }
          if (inferredAcaoId) break;
        }
      }
    }
  }

  return {
    _idx: i,
    _dadosFnde: d,
    _progMatchada: matched,
    _selected: parseBRL(getValorFnde(d, 'Valor Custeio')) + parseBRL(getValorFnde(d, 'Valor Capital')) > 0,
    idPrograma,
    idAcao: inferredAcaoId || (acoesDisp.length === 1 ? String(acoesDisp[0].id) : ''),
    idConta,
    // Normalize destinacao: prefer the part after '/' when present (e.g. "PDDE / PDDE Basico - 2 Parcela" -> "PDDE Basico - 2 Parcela")
    destinacao: (function() {
      const raw = String(getValorFnde(d, 'Destinação') || '').trim();
      if (!raw) return '';
      const text = raw.includes('/') ? raw.split('/').pop().trim() : raw;
      return text.replace(/[^\p{L}\p{N}\p{M}\s\u00AA\u00BA-]/gu, ' ').replace(/\s+/g, ' ').trim();
    })(),
    vlrC: getValorFnde(d, 'Valor Custeio'),
    vlrK: getValorFnde(d, 'Valor Capital'),
    data: toIso(getValorFnde(d, 'Data da Ord')),
  };
}

function ExportarReceitaFndeModal({ show, onClose, onSubmit, dados = [], programas = [], acoes = [], contas = [] }) {
  const [linhas, setLinhas] = useState([]);
  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  // Função para remover caracteres especiais indesejados
  // Mantém letras acentuadas (ex.: ç, ã, é) e ordinais (ª, º) — usados em "1ª Parcela", "1º Trimestre"
  function sanitizeDestinacao(text) {
    return text
      .replace(/[^\p{L}\p{N}\p{M}\s\u00AA\u00BA-]/gu, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }
  useEffect(() => {
    if (show && dados.length > 0) {
      const built = dados.map((d, i) => buildLinha(d, i, programas, acoes, contas));
      // Log para diagnóstico: quais textos de Programa chegaram e qual foi o candidato
      try {
        built.forEach(b => {
          const progTxt = getValorFnde(b._dadosFnde, 'Programa') || getValorFnde(b._dadosFnde, 'Programa *') || '';
          const progNorm = normalizeText(progTxt);
          const matched = b._progMatchada ? b._progMatchada.descricao : null;
          console.log('FNDE Programa:', progTxt, '-> norm:', progNorm, 'matched:', matched);
        });
      } catch (e) { /* ignore */ }
      setLinhas(built);
    }
  }, [show, dados, programas.length, acoes.length, contas.length]);

  function toggleSelectAll(checked) {
    setLinhas(prev => prev.map(l => ({ ...l, _selected: checked })));
  }

  function handleLinha(idx, field, value) {
    setLinhas(prev => prev.map(l => {
      if (l._idx !== idx) return l;
      const updated = { ...l };
      if (field === 'destinacao') {
        updated.destinacao = sanitizeDestinacao(value);
      } else if (field === '_selected') {
        updated._selected = !!value;
      } else {
        updated[field] = value;
      }
      if (field === 'idPrograma') {
        updated.idAcao = '';
        updated.idConta = '';
        // Auto-set ação e conta se houver apenas uma
        const acoesDisp = acoes.filter(a => String(a.idPrograma) === String(value));
        const contasDisp = contas.filter(c => String(c.idPrograma) === String(value) && c.debitar === 'SIM');
        if (acoesDisp.length === 1) updated.idAcao = String(acoesDisp[0].id);
        if (contasDisp.length === 1) {
          updated.idConta = contasDisp[0].numConta;
        } else {
          // Reaproveitar a conta informada pela página do FNDE ao trocar de programa
          const alvo = normalizeConta(getContaFnde(l._dadosFnde));
          if (alvo) {
            const match = contasDisp.find(c => normalizeConta(c.numConta) === alvo)
              || contas.find(c => String(c.idPrograma) === String(value) && normalizeConta(c.numConta) === alvo);
            if (match) updated.idConta = match.numConta;
          }
        }
      }
      return updated;
    }));
  }

  function handleSubmit(e) {
    e.preventDefault();
    const selecionadas = linhas.filter(l => l._selected);
    if (selecionadas.length === 0) {
      showDefaultModal('warning', 'Selecione ao menos um registro para exportar.');
      return;
    }
    const invalidas = selecionadas.filter(l => !l.idPrograma || !l.idAcao || !l.idConta || !(l.destinacao || '').trim());
    if (invalidas.length > 0) {
      const faltando = invalidas.map(l => `Linha ${l._idx + 1}`).join(', ');
      showDefaultModal('warning', `Preencha Programa, Ação, Conta e Destinação nas linhas selecionadas.\nFaltando: ${faltando}`);
      return;
    }

    // Alerta: conta selecionada diferente da conta informada pelo FNDE
    const divergentes = selecionadas.filter(l => contaDivergente(getContaFnde(l._dadosFnde), l.idConta));
    if (divergentes.length > 0) {
      const detalhes = divergentes.map(l =>
        `• Linha ${l._idx + 1} — FNDE: ${getContaFnde(l._dadosFnde)} | Selecionada: ${l.idConta}` +
        `${l.destinacao ? ` (${l.destinacao})` : ''}`
      ).join('\n');
      showConfirmModal(
        'contaDivergente',
        () => onSubmit(selecionadas),
        `${divergentes.length} linha(s) com conta diferente da conta informada pelo FNDE:\n\n` +
        `${detalhes}\n\nDeseja exportar mesmo assim?`
      );
      return;
    }
    onSubmit(selecionadas);
  }

  if (!show) return null;

  // Detecta quais colunas FNDE estão presentes nos dados
  const colunasPresentes = dados.length > 0
    ? COLUNAS_FNDE.filter(col =>
        Object.keys(dados[0]).some(k => k.toLowerCase().includes(col.toLowerCase()))
      )
    : [];

  return (
    <div className="modal show d-block" tabIndex="-1"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1055, position: 'fixed', inset: 0 }}>
      <div className="modal-dialog modal-dialog-scrollable" style={{ maxWidth: '90vw', maxHeight: '90vh', margin: '5vh auto' }}>
        <div className="modal-content" style={{ height: '88vh' }}>
          <div className="modal-header bg-success text-white" style={{ padding: '0.35rem 1rem' }}>
            <h6 className="modal-title mb-0 small">
              <i className="bi bi-box-arrow-in-down me-2"></i>
              EXPORTAR RECEITAS FNDE — {linhas.length} registro(s) — {linhas.filter(l => l._selected).length} selecionado(s)
              {linhas.filter(l => l._selected && contaDivergente(getContaFnde(l._dadosFnde), l.idConta)).length > 0 && (
                <span className="badge bg-warning text-dark ms-2">
                  <i className="bi bi-exclamation-triangle-fill me-1"></i>
                  {linhas.filter(l => l._selected && contaDivergente(getContaFnde(l._dadosFnde), l.idConta)).length} conta(s) divergente(s)
                </span>
              )}
            </h6>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <div className="modal-body p-2">
            <form onSubmit={handleSubmit} id="formExportarReceita" noValidate>
              <div className="table-responsive">
                <table className="table table-bordered table-sm" style={{ fontSize: '0.78rem' }}>
                  <thead className="table-success">
                    <tr>
                      <th style={{ width: 28 }} className="text-center">
                        <input type="checkbox" className="form-check-input" checked={linhas.length > 0 && linhas.every(l => l._selected)} onChange={e => toggleSelectAll(e.target.checked)} />
                      </th>
                      <th style={{ minWidth: 28 }}>#</th>
                      {/* Colunas FNDE selecionadas */}
                      {colunasPresentes.map(col => (
                        <th
                          key={col}
                          className="text-nowrap"
                          style={col === 'Data da Ord. de Pagamento' ? { minWidth: 45 } : {}}
                        >
                          {col}
                        </th>
                      ))}
                      {/* Campos de exportação */}
                      <th style={{ minWidth: 140 }} className="text-danger">Programa *</th>
                      <th style={{ minWidth: 160 }} className="text-danger">Ação *</th>
                      <th style={{ minWidth: 130 }} className="text-danger">Conta *</th>
                      <th style={{ minWidth: 280 }} className="text-danger">Destinação *</th>
                    </tr>
                  </thead>
                  <tbody>
                    {linhas.map((l, i) => {
                      const acoesDisp = acoes.filter(a => String(a.idPrograma) === String(l.idPrograma));
                      const contasDisp = contas.filter(c => String(c.idPrograma) === String(l.idPrograma) && c.debitar === 'SIM');
                      const contaFnde = getContaFnde(l._dadosFnde);
                      const divergente = contaDivergente(contaFnde, l.idConta);
                      return (
                        <tr key={l._idx}>
                          <td className="text-center">
                            {(() => {
                              const sum = parseBRL(l.vlrC) + parseBRL(l.vlrK);
                              const disabled = sum <= 0;
                              return (
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={!!l._selected}
                                  disabled={disabled}
                                  onChange={e => handleLinha(l._idx, '_selected', e.target.checked)}
                                  title={disabled ? 'Valor Custeio + Capital = 0' : ''}
                                />
                              );
                            })()}
                          </td>
                          <td className="text-muted text-center">{i + 1}</td>
                          {/* Colunas FNDE selecionadas */}
                          {colunasPresentes.map(col => (
                            <td
                              key={col}
                              className="text-nowrap"
                              style={col === 'Data da Ord. de Pagamento' ? { minWidth: 40 } : {}}
                            >
                              {col === 'Destinação' ? null : (getValorFnde(l._dadosFnde, col) || '-')}
                            </td>
                          ))}
                          {/* Programa: texto se foi casado, senão select */}
                          <td>
                            {l._progMatchada ? (
                              <input
                                type="text"
                                className="form-control form-control-sm bg-light"
                                value={l._progMatchada.descricao}
                                readOnly
                              />
                            ) : (
                              <select
                                className="form-select form-select-sm"
                                value={l.idPrograma}
                                onChange={e => handleLinha(l._idx, 'idPrograma', e.target.value)}
                              >
                                <option value="">Selecione...</option>
                                {programas.map(p => (
                                  <option key={p.id} value={p.id}>{p.descricao}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          {/* Ação */}
                          <td>
                            {acoesDisp.length === 1 ? (
                              <input
                                type="text"
                                className="form-control form-control-sm bg-light"
                                value={acoesDisp[0].descricao}
                                readOnly
                              />
                            ) : (
                              <select
                                className="form-select form-select-sm"
                                value={l.idAcao}
                                onChange={e => handleLinha(l._idx, 'idAcao', e.target.value)}
                                disabled={!l.idPrograma}
                              >
                                <option value="">{l.idPrograma ? 'Selecione...' : 'Selecione programa'}</option>
                                {acoesDisp.map(a => (
                                  <option key={a.id} value={a.id}>{a.descricao}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          {/* Conta */}
                          <td title={contaFnde ? `Conta informada pelo FNDE: ${contaFnde}` : ''}>
                            <div className="d-flex align-items-center gap-1">
                              {contasDisp.length === 1 ? (
                                <input
                                  type="text"
                                  className={`form-control form-control-sm bg-light${divergente ? ' border-warning' : ''}`}
                                  value={contasDisp[0].numConta}
                                  readOnly
                                />
                              ) : (
                                <select
                                  className={`form-select form-select-sm${divergente ? ' border-warning' : ''}`}
                                  value={l.idConta}
                                  onChange={e => handleLinha(l._idx, 'idConta', e.target.value)}
                                  disabled={!l.idPrograma}
                                >
                                  <option value="">{l.idPrograma ? 'Selecione...' : 'Selecione programa'}</option>
                                  {contasDisp.map(c => (
                                    <option key={c.numConta} value={c.numConta}>{c.numConta}</option>
                                  ))}
                                </select>
                              )}
                              {divergente && (
                                <i className="bi bi-exclamation-triangle-fill text-warning"
                                  title={`Conta do FNDE: ${contaFnde} — conta selecionada para débito: ${l.idConta}. Confirme antes de exportar.`}></i>
                              )}
                            </div>
                          </td>
                          {/* Destinação editável */}
                          <td style={{ minWidth: 280 }}>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={l.destinacao || ''}
                              onChange={e => handleLinha(l._idx, 'destinacao', e.target.value)}
                              maxLength={120}
                              placeholder="Digite a destinação"
                              style={{ minWidth: 260 }}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </form>
          </div>
          <div className="modal-footer py-2">
            <span className="text-muted small me-auto">
              <i className="bi bi-info-circle me-1"></i>
              Preencha Programa, Ação, Conta e Destinação em cada linha antes de exportar.
            </span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Cancelar</button>
            <button type="submit" form="formExportarReceita" className="btn btn-success btn-sm">
              <i className="bi bi-box-arrow-in-down me-1"></i>
              Exportar para Receita
            </button>
          </div>
        </div>
      </div>
      <DefaultModal {...setDefaultModal} />
      <ConfirmModal {...setConfirmModal} />
    </div>
  );
}

export default ExportarReceitaFndeModal;
